import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useModal } from '../../components/layout';
import {Edit, Play, Plus, Users, FileText, Globe, Code, Copy} from 'lucide-react';
import { Badge, Card, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { KnowledgeBase, User } from '../../types/knowledge-base.ts';
import { API_ENDPOINTS } from '../../config/api.config';
import AssignRAGGroupsModal from '../../components/modals/common/AssignGroupsModal.tsx';
import { apiService } from '../../services/apiService';

import '../../../scss/custom.scss';
import ContentSkeleton from "@/components/loading/ContentSkeleton.tsx";

interface Group {
    id: string;
    name: string;
}

interface GroupResponse {
    data: Group[];
}

interface KnowledgeBaseResponse {
    knowledgeBases: KnowledgeBase[];
    total: number;
    page: number;
    totalPages: number;
    limit: number;
}

interface PaginationData {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
}

const ITEMS_PER_PAGE = 10;

const KnowledgeBasesPage: React.FC = () => {
    const navigate = useNavigate();
    const { user: authUser } = useAuth();
    const [user] = useState<User | null>(authUser);
    const [selectedKB, setSelectedKB] = useState<KnowledgeBase | null>(null);
    const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
    const [isLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [showGroupsModal, setShowGroupsModal] = useState(false);
    const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
    const [groups, setGroups] = useState<Group[]>([]);
    const [isUpdatingGroups, setIsUpdatingGroups] = useState(false);
    const modalState = useModal();
    const [currentPage, setCurrentPage] = useState(1);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [isContentLoading, setIsContentLoading] = useState(false);
    const [pagination, setPagination] = useState<PaginationData>({
        page: 1,
        limit: ITEMS_PER_PAGE,
        total: 0,
        totalPages: 0
    });


    const renderSourceCount = (kb: KnowledgeBase) => {
        const count = kb.data_source_ids?.length || 0;

        let sourceText;
        switch (kb.source_type) {
            case 'files':
                sourceText = `${count} ${count === 1 ? 'file' : 'files'}`;
                break;
            case 'websites':
                sourceText = `${count} ${count === 1 ? 'website' : 'websites'}`;
                break;
            case 'apis':
                sourceText = `${count} ${count === 1 ? 'API' : 'APIs'}`;
                break;
            default:
                return null;
        }

        return sourceText;
    };

    const fetchKnowledgeBases = async (page: number, search?: string) => {
        try {
            if (isInitialLoading) {
                setIsContentLoading(false);
            } else {
                setIsContentLoading(true);
            }
            setError(null);

            const queryParams = new URLSearchParams({
                page: page.toString(),
                limit: ITEMS_PER_PAGE.toString(),
                ...(search ? { search } : {})
            });

            const response = await apiService.get<KnowledgeBaseResponse>(
                `${API_ENDPOINTS.INDEXES.LIST}?${queryParams}`
            );

            if (response && Array.isArray(response.knowledgeBases)) {
                setKnowledgeBases(response.knowledgeBases);
                setCurrentPage(response.page || 1);
                setPagination({
                    page: response.page || 1,
                    limit: response.limit || ITEMS_PER_PAGE,
                    total: response.total || 0,
                    totalPages: response.totalPages || 1
                });
            } else {
                setKnowledgeBases([]);
                setError('Invalid response format from server');
            }
        } catch (error) {
            console.error('Failed to fetch knowledge-bases:', error);
            setError('Failed to load knowledge bases');
            setKnowledgeBases([]);
            setPagination({
                page: 1,
                limit: ITEMS_PER_PAGE,
                total: 0,
                totalPages: 1
            });
        } finally {
            setIsInitialLoading(false);
            setIsContentLoading(false);
        }
    };

    useEffect(() => {
        fetchKnowledgeBases(1);
    }, []);

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
        fetchKnowledgeBases(newPage, searchQuery);
    };

    const handleSearch = (value: string) => {
        setSearchQuery(value);
        setCurrentPage(1);
        fetchKnowledgeBases(1, value);
    };

    const handleSelectKB = (kb: KnowledgeBase) => {
        setSelectedKB(kb.id === selectedKB?.id ? null : kb);
    };

    const fetchGroups = async (): Promise<void> => {
        try {
            const queryParams = new URLSearchParams({
                all: 'true',
                sort_by: 'name',
                sort_order: 'ASC'
            });

            const url = `${API_ENDPOINTS.GROUPS.LIST}?${queryParams}`;
            const result = await apiService.get<GroupResponse>(url);
            setGroups(result.data ?? []); // Use nullish coalescing
        } catch (error) {
            console.error('Failed to fetch groups:', error);
            modalState.setError('Failed to load groups');
            setGroups([]);
        }
    };

    const handleGroupsClick = async (kb: KnowledgeBase): Promise<void> => {
        setSelectedKB(kb);
        setShowGroupsModal(true);

        try {
            await fetchGroups();

            const groupsUrl = `${API_ENDPOINTS.INDEXES.GROUP_ACCESS}/${kb.id}`;
            const data = await apiService.get<{ groups: Group[] }>(groupsUrl);

            // Use optional chaining and type guard
            const groupIds = data?.groups?.map(g => g.id).filter((id): id is string => id != null) ?? [];
            setSelectedGroupIds(groupIds);
        } catch (error) {
            console.error('Failed to fetch group assignments:', error);
            modalState.setError('Failed to load group assignments');
            setSelectedGroupIds([]);
            setShowGroupsModal(false);
        }
    };

    const handleGroupsConfirm = async (): Promise<void> => {
        if (!selectedKB) return;

        setIsUpdatingGroups(true);
        try {
            await apiService.put(`${API_ENDPOINTS.INDEXES.GROUP_ACCESS}/${selectedKB.id}`, {
                groupIds: selectedGroupIds
            });

            modalState.setSuccess('Groups updated successfully');
            setShowGroupsModal(false);
            setSelectedKB(null);
            setSelectedGroupIds([]);

            await fetchKnowledgeBases(currentPage, searchQuery);
        } catch (error) {
            console.error('Failed to update group assignments:', error);
            modalState.setError(error instanceof Error ? error.message : 'Failed to update group assignments');
        } finally {
            setIsUpdatingGroups(false);
        }
    };

    const handleGroupSelectionChange = (groupId: string, checked: boolean): void => {
        setSelectedGroupIds(prevIds => {
            if (checked) {
                return [...prevIds, groupId];
            }
            return prevIds.filter(id => id !== groupId);
        });
    };

    const handleDeploy = async (id: string) => {
        try {
            setKnowledgeBases(prevKBs =>
                prevKBs.map(kb =>
                    kb.id === id ? { ...kb, status: 'processing' } : kb
                )
            );

            await apiService.post(`${API_ENDPOINTS.INDEXES.DEPLOY}/${id}`, {});
            await fetchKnowledgeBases(currentPage, searchQuery);
            modalState.setSuccess('Knowledge base deployment initiated');
        } catch (error) {
            console.error('Deploy error:', error);
            modalState.setError(error instanceof Error ? error.message : 'Failed to deploy knowledge base');
            await fetchKnowledgeBases(currentPage, searchQuery);
        }
    };

    const renderPagination = () => {
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        const endPage = Math.min(pagination.totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        const pages = [];
        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                    <button
                        className="page-link"
                        onClick={() => handlePageChange(i)}
                    >
                        {i}
                    </button>
                </li>
            );
        }

        return (
            <nav aria-label="Knowledge bases pagination">
                <ul className="pagination mb-0 justify-content-end">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                    </li>
                    {startPage > 1 && (
                        <>
                            <li className="page-item">
                                <button className="page-link" onClick={() => handlePageChange(1)}>1</button>
                            </li>
                            {startPage > 2 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}
                        </>
                    )}
                    {pages}
                    {endPage < pagination.totalPages && (
                        <>
                            {endPage < pagination.totalPages - 1 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}
                            <li className="page-item">
                                <button
                                    className="page-link"
                                    onClick={() => handlePageChange(pagination.totalPages)}
                                >
                                    {pagination.totalPages}
                                </button>
                            </li>
                        </>
                    )}
                    <li className={`page-item ${currentPage === pagination.totalPages ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === pagination.totalPages}
                        >
                            Next
                        </button>
                    </li>
                </ul>
            </nav>
        );
    };

    if (!user) return null;


    const handleClone = (kb: KnowledgeBase) => {
        // Encode the KB data to pass via navigation state
        const cloneData = {
            name: `${kb.name} (Clone)`,
            description: kb.description || '',
            sourceType: kb.source_type,
            chunkSize: kb.chunk_size,
            overlap: kb.overlap,
            embeddingModel: kb.embedding_model,
            dataSourceIds: kb.data_source_ids
        };

        // Navigate to create page with pre-populated data
        navigate('/knowledge-bases/create', { state: { cloneData } });
    };


    if (modalState.isLoading) {
        return <ContentSkeleton />;
    }


    return (
        <div className="page-container">
            <div className="mb-4">
                <h5 className="mb-3">Knowledge Bases</h5>
                {(isInitialLoading || modalState.isLoading) ? (
                    <ContentSkeleton />
                ) : (
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Body className="p-4">
                            {error && (
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                            )}
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <div className="flex-grow-1">
                                    <div className="mt-3">
                                        <Form.Group>
                                            <div className="d-flex gap-2">
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Search knowledge bases..."
                                                    value={searchQuery}
                                                    onChange={(e) => handleSearch(e.target.value)}
                                                    className="border"
                                                    style={{ height: '38px' }}
                                                />
                                                <button
                                                    className="btn btn-outline-primary"
                                                        onClick={() => navigate('/knowledge-bases/create')}
                                                        style={{ height: '38px', whiteSpace: 'nowrap' }}
                                                    >
                                                        <Plus size={16} className="me-1" />
                                                        New Knowledge Base
                                                    </button>
                                                </div>
                                            </Form.Group>
                                        </div>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-8">
                                        {isContentLoading ? (
                                            <div className="text-center p-2">
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            </div>
                                        ) : knowledgeBases.length === 0 ? (
                                            <div className="text-center py-4 text-muted">
                                                No knowledge bases found.
                                            </div>
                                        ) : (
                                            <>
                                                <div className="list-group">
                                                    {knowledgeBases.map((kb) => (
                                                        <div
                                                            key={kb.id}
                                                            className={`list-group-item py-2 px-3 ${
                                                                selectedKB?.id === kb.id ? 'selected' : ''
                                                            }`}
                                                            onClick={() => handleSelectKB(kb)}
                                                        >
                                                            <div className="d-flex">
                                                                <div className="me-2">
                                                                    {kb.source_type === 'files' ? (
                                                                        <FileText size={18} className="text-muted" />
                                                                    ) : kb.source_type === 'websites' ? (
                                                                        <Globe size={18} className="text-muted" />
                                                                    ) : (
                                                                        <Code size={18} className="text-muted" />
                                                                    )}
                                                                </div>

                                                                <div className="flex-grow-1 min-w-0">
                                                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                                                        <h6 className="mb-0 text-truncate">{kb.name}</h6>
                                                                        <Badge
                                                                            className={`ms-2 px-2 py-1 text-xs ${
                                                                                kb.status === 'ready' ? 'bg-success-subtle text-success-emphasis' :
                                                                                    kb.status === 'processing' ? 'bg-warning-subtle text-warning-emphasis' :
                                                                                        'bg-danger-subtle text-danger-emphasis'
                                                                            }`}
                                                                        >
                                                                            {kb.status.charAt(0).toUpperCase() + kb.status.slice(1)}
                                                                        </Badge>
                                                                    </div>
                                                                    <div className="text-muted small">
                                                                        {renderSourceCount(kb)}
                                                                    </div>

                                                                    <div className="d-flex align-items-end">
                                                                        <div className="flex-grow-1">
                                                                            {kb.description && (
                                                                                <p className="text-muted small mb-1" style={{ whiteSpace: 'pre-line' }}>
                                                                                    {kb.description}
                                                                                </p>
                                                                            )}
                                                                            <div className="text-muted small">
                                                                                {kb.created_by && (
                                                                                    <span>{kb.created_by}</span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                        <div className="text-muted small ms-3 text-nowrap">
                                                                            {kb.created_at === kb.updated_at ? (
                                                                                <>Created on {new Date(kb.created_at).toLocaleDateString('en-US', {
                                                                                    year: 'numeric',
                                                                                    month: 'short',
                                                                                    day: 'numeric'
                                                                                })}</>
                                                                            ) : (
                                                                                <>Updated on {new Date(kb.updated_at).toLocaleDateString('en-US', {
                                                                                    year: 'numeric',
                                                                                    month: 'short',
                                                                                    day: 'numeric'
                                                                                })}</>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>

                                                <div className="d-flex justify-content-between align-items-center mt-5">
                                                    <div className="text-muted small">
                                                        Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
                                                    </div>
                                                    {!isLoading && pagination.totalPages > 0 && renderPagination()}
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    <div className="col-md-4">
                                        <Card className="border shadow-sm">
                                            <Card.Header className="border-bottom px-3 py-2">
                                                <h6 className="mb-0">Actions</h6>
                                            </Card.Header>
                                            <Card.Body className="p-2">
                                                <div className="d-grid gap-1">
                                                    <OverlayTrigger
                                                        placement="left"
                                                        overlay={
                                                            selectedKB?.status === 'ready' ? (
                                                                <Tooltip>
                                                                    Only name and description can be modified
                                                                </Tooltip>
                                                            ) : <></>
                                                        }
                                                    >
                                                        <button
                                                            className="w-100 d-flex align-items-center justify-content-center gap-2 px-3 py-1 border rounded text-sm fw-medium btn btn-outline-secondary text-dark action-button"
                                                            disabled={!selectedKB}
                                                            onClick={() => selectedKB && navigate(`/knowledge-bases/${selectedKB.id}/edit`)}
                                                        >
                                                            <Edit size={16} />
                                                            Edit
                                                        </button>
                                                    </OverlayTrigger>
                                                    <button
                                                        className="w-100 d-flex align-items-center justify-content-center gap-2 px-3 py-1 border rounded text-sm fw-medium btn btn-outline-secondary text-dark action-button"
                                                        disabled={!selectedKB}
                                                        onClick={() => selectedKB && handleGroupsClick(selectedKB)}
                                                    >
                                                        <Users size={16} />
                                                        Access
                                                    </button>
                                                    <button
                                                        className="w-100 d-flex align-items-center justify-content-center gap-2 px-3 py-1 border rounded text-sm fw-medium btn btn-outline-secondary text-dark action-button"
                                                        disabled={!selectedKB || selectedKB.status === 'processing' || selectedKB.status === 'ready'}
                                                        onClick={() => selectedKB && handleDeploy(selectedKB.id)}
                                                    >
                                                        <Play size={16} />
                                                        Deploy
                                                    </button>
                                                    <button
                                                        className="w-100 d-flex align-items-center justify-content-center gap-2 px-3 py-1 border rounded text-sm fw-medium btn btn-outline-secondary text-dark action-button"
                                                        disabled={!selectedKB}
                                                        onClick={() => selectedKB && handleClone(selectedKB)}
                                                    >
                                                        <Copy size={16} />
                                                        Clone
                                                    </button>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    )}
                </div>

                {selectedKB && (
                    <AssignRAGGroupsModal
                        show={showGroupsModal}
                        application={selectedKB}
                        groups={groups}
                        selectedGroupIds={selectedGroupIds}
                        isUpdatingGroups={isUpdatingGroups}
                        onHide={() => {
                            setShowGroupsModal(false);
                            setSelectedKB(null);
                            setSelectedGroupIds([]);
                        }}
                        onConfirm={handleGroupsConfirm}
                        onGroupSelectionChange={handleGroupSelectionChange}
                    />
                )}
        </div>
    );
};

export default KnowledgeBasesPage;